import { Box, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { ChartOptions } from 'chart.js'
import 'chart.js/auto'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { getSeverityColor } from './_helpers'
import useDashboardListFilters from '../../../util/useDashboardListFilters'
import { useDashboardGraph } from '../../../queries/hooks/useDashboardGraph'
import { useDashboardGraphEvents } from '../../../queries/hooks/useDashboardGraphEvents'
import { getDashboardQueryContext } from '../../../queries/hooks/dashboardHookUtils'
import {
  type DashboardTimeGranularity,
  clampToFloor,
  formatUtcDate,
  getBucketRangeForGranularity,
  getDashboardTimezone,
  getNextGranularity,
  getQuarterDaySegment,
  getRangeFromDateAndHour,
  parseGraphDate,
} from '../../../util/dateUtils'
import { getSeverityCount } from '../../../util/severityCounts'

type GraphPoint = {
  timestamp: string
  low: number
  medium: number
  critical: number
  kind: 'daily' | 'segment'
  sourceDate?: string
  sourceHour?: number
}

const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
}
const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
}
const FULL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
}
const ONE_DAY_HOURS = 24
// Minimum horizontal space reserved per data point. When the points need more
// room than the card provides, the graph scrolls horizontally instead of
// squishing/dropping points so every point (and its click button) stays visible.
const POINT_SCROLL_WIDTH = 48
const QUARTER_DAY_LABEL_BY_SEGMENT: Record<number, string> = {
  0: 'Night',
  1: 'Morning',
  2: 'Afternoon',
  3: 'Evening',
}

/**
 * Sparse-data views pad the series with placeholder points that carry `null` across
 * every dataset. A tick is a padding placeholder only when every series is empty at
 * that index, so we key off the data rather than the label text. This prevents
 * legitimate all-digit labels (e.g. year granularity "2026") from being hidden.
 * Returns false when there are no datasets, since padding cannot be inferred.
 */
export const isPaddingTickIndex = (
  datasets: Array<{ data: Array<number | null | undefined> }>,
  index: number
): boolean =>
  datasets.length > 0 &&
  datasets.every(
    (dataset) => dataset.data[index] === null || dataset.data[index] === undefined
  )

/**
 * Formats a date for compact graph labels/range subtitles (e.g., "Jun 18").
 */
const formatShortDate = (date: Date, timezone: string) =>
  date.toLocaleDateString(undefined, { ...SHORT_DATE_OPTIONS, timeZone: timezone })

const formatShortTime = (date: Date, timezone: string) =>
  date.toLocaleTimeString(undefined, { ...TIME_OPTIONS, timeZone: timezone })

const getMonthDayInTimezone = (date: Date, timezone: string) => {
  const parts = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  }).formatToParts(date)
  const month = parts.find((part) => part.type === 'month')?.value
  const day = parts.find((part) => part.type === 'day')?.value

  return {
    month: month ?? date.toLocaleDateString(undefined, { month: 'short', timeZone: timezone }),
    day: day ?? date.toLocaleDateString(undefined, { day: 'numeric', timeZone: timezone }),
  }
}

/**
 * Chooses the initial graph granularity from the active selected date range span.
 */
const getAutoGranularity = (from: Date, to: Date): DashboardTimeGranularity => {
  const diffMs = to.getTime() - from.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  const diffMonths = diffDays / 30.44
  const diffYears = diffMonths / 12
  const diffHours = diffMs / (1000 * 60 * 60)

  if (diffYears >= 2) return 'year'
  if (diffYears >= 1) return 'quarter'
  if (diffMonths >= 4) return 'month'
  if (diffMonths >= 1) return 'week'
  // Treat ~1 week windows as day granularity so drilldown stays week -> day.
  if (diffDays >= 6.5) return 'day'
  // Full-day windows are often 23:59:59.999, so use a tolerant threshold.
  // This preserves week -> day -> part of day -> event drilldown.
  if (diffDays >= 0.95) return 'quarter_day'
  // Treat sub-day windows as hourly before falling to event.
  if (diffHours >= 1) return 'hour'
  return 'event'
}

const DashboardGraph: React.FC = () => {
  const { filters, updateFilters } = useDashboardListFilters()
  const timezone = getDashboardTimezone()
  const {
    resolvedDateRange: { fromDate: resolvedFromDate, toDate: resolvedToDate, fromMs, toMs },
  } = getDashboardQueryContext(filters, timezone)
  const { data: graphData, isLoading, isFetching, isError } = useDashboardGraph()
  const graph = graphData?.graph
  const isDayGranularitySeries = graph?.granularity === 'day'
  // The served series decides the floor: `noVisitDate` makes the API range on
  // the audited date even in date-of-service mode, and that series has hours.
  const granularityFloor: DashboardTimeGranularity = isDayGranularitySeries ? 'day' : 'event'
  const [selectedTimePoint, setSelectedTimePoint] = useState<string | null>(null)
  const [currentGranularity, setCurrentGranularity] =
    useState<DashboardTimeGranularity>('week')
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null)
  const [eventQueryParams, setEventQueryParams] = useState<{ date: string; hour: number } | null>(
    null
  )
  const isEventGranularity = currentGranularity === 'event'
  const {
    data: graphEvents,
    isLoading: isEventsLoading,
    isFetching: isEventsFetching,
    isError: isEventsError,
  } = useDashboardGraphEvents({
    enabled: isEventGranularity,
    date: eventQueryParams?.date,
    hour: eventQueryParams?.hour,
  })

  useEffect(() => {
    if (fromMs === undefined || toMs === undefined) return
    const from = new Date(fromMs)
    const to = new Date(toMs)
    setCurrentGranularity(clampToFloor(getAutoGranularity(from, to), granularityFloor))
    if (lastClickedIndex === null) setSelectedTimePoint(null)
    setLastClickedIndex(null)
  }, [fromMs, toMs, lastClickedIndex, granularityFloor])

  const basePoints = useMemo(() => {
    const points: GraphPoint[] = []
    for (const entry of graph?.seriesByDate ?? []) {
      const dayStart = parseGraphDate(entry.date)
      // A date-of-service series carries its totals on the entry and has no
      // sub-day slices to plot.
      const allHourlySlices = isDayGranularitySeries
        ? []
        : [...entry.night, ...entry.morning, ...entry.afternoon, ...entry.evening]
      const dayTotalSlices = isDayGranularitySeries
        ? [{ hour: 0, alerts: entry.alerts ?? [] }]
        : allHourlySlices

      points.push({
        timestamp: dayStart.toISOString(),
        low: dayTotalSlices.reduce((sum, slice) => sum + getSeverityCount(slice, '1'), 0),
        medium: dayTotalSlices.reduce((sum, slice) => sum + getSeverityCount(slice, '2'), 0),
        critical: dayTotalSlices.reduce((sum, slice) => sum + getSeverityCount(slice, '3'), 0),
        kind: 'daily',
        sourceDate: entry.date,
      })

      allHourlySlices.forEach((slice) => {
        const hourPoint = new Date(dayStart)
        hourPoint.setUTCHours(slice.hour, 0, 0, 0)

        points.push({
          timestamp: hourPoint.toISOString(),
          low: getSeverityCount(slice, '1'),
          medium: getSeverityCount(slice, '2'),
          critical: getSeverityCount(slice, '3'),
          kind: 'segment',
          sourceDate: entry.date,
          sourceHour: slice.hour,
        })
      })
    }
    return points
  }, [graph?.seriesByDate, isDayGranularitySeries])

  const eventPoints = useMemo(() => {
    const points: GraphPoint[] = []

    ;(graphEvents ?? []).forEach((item) => {
      const parsedTime = new Date(item.timestamp).getTime()
      if (Number.isNaN(parsedTime)) {
        return
      }

      points.push({
        timestamp: new Date(parsedTime).toISOString(),
        low: getSeverityCount(item.alerts, '1'),
        medium: getSeverityCount(item.alerts, '2'),
        critical: getSeverityCount(item.alerts, '3'),
        kind: 'segment',
      })
    })

    return points
  }, [graphEvents])

  const pointsForGranularity = useMemo(() => {
    if (currentGranularity === 'event') {
      return eventPoints
    }
    if (currentGranularity === 'quarter_day' || currentGranularity === 'hour') {
      return basePoints.filter((p) => p.kind === 'segment')
    }
    return basePoints.filter((p) => p.kind === 'daily')
  }, [basePoints, currentGranularity, eventPoints])

  /**
   * Formats a bucket timestamp label for the x-axis and point tooltips
   * based on the current granularity.
   */
  const formatTimestamp = useCallback(
    (timestamp: string, granularity: DashboardTimeGranularity) => {
      const date = new Date(timestamp)
      if (granularity === 'year') return `${date.getUTCFullYear()}`
      if (granularity === 'quarter')
        return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`
      if (granularity === 'month')
        return date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          timeZone: 'UTC',
        })
      if (granularity === 'week') {
        const monday = new Date(date)
        const diff = monday.getUTCDay() === 0 ? -6 : 1 - monday.getUTCDay()
        monday.setUTCDate(monday.getUTCDate() + diff)
        const sunday = new Date(monday)
        sunday.setUTCDate(monday.getUTCDate() + 6)
        // Week buckets are anchored at UTC midnight (getUTCDay/setUTCDate above),
        // so label the endpoints in UTC too — matching the month/quarter/year
        // branches. Formatting these UTC instants in the user's timezone would
        // shift the label a day early for negative-offset zones.
        const mondayParts = getMonthDayInTimezone(monday, 'UTC')
        const sundayParts = getMonthDayInTimezone(sunday, 'UTC')
        return mondayParts.month === sundayParts.month
          ? `${mondayParts.month} ${mondayParts.day}-${sundayParts.day}`
          : `${mondayParts.month} ${mondayParts.day}-${sundayParts.month} ${sundayParts.day}`
      }
      if (granularity === 'day')
        return date.toLocaleDateString(undefined, { ...SHORT_DATE_OPTIONS, timeZone: timezone })
      if (granularity === 'quarter_day') {
        let part = 'Evening'
        if (date.getUTCHours() < 6) part = 'Night'
        else if (date.getUTCHours() < 12) part = 'Morning'
        else if (date.getUTCHours() < 18) part = 'Afternoon'
        return `${date.toLocaleDateString(undefined, { ...SHORT_DATE_OPTIONS, timeZone: timezone })} ${part}`
      }
      if (granularity === 'hour' || granularity === 'event')
        return `${date.toLocaleDateString(undefined, { ...SHORT_DATE_OPTIONS, timeZone: timezone })} ${date.toLocaleTimeString(undefined, { ...TIME_OPTIONS, timeZone: timezone })}`
      return date.toLocaleDateString(undefined, { ...SHORT_DATE_OPTIONS, timeZone: timezone })
    },
    [timezone]
  )

  const processed = useMemo(() => {
    const from = fromMs ?? 0
    const to = toMs ?? Number.MAX_SAFE_INTEGER

    // Event view should render directly from graph/events response rows.
    if (currentGranularity === 'event') {
      const eventRows = [...pointsForGranularity].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )

      return {
        keys: eventRows.map((point, index) => `${new Date(point.timestamp).getTime()}-${index}`),
        labels: eventRows.map((point) => formatTimestamp(point.timestamp, 'event')),
        low: eventRows.map((point) => point.low),
        medium: eventRows.map((point) => point.medium),
        critical: eventRows.map((point) => point.critical),
        rawTs: eventRows.map((point) => point.timestamp),
        rawDate: eventRows.map(() => undefined),
        rawHour: eventRows.map(() => undefined),
      }
    }

    const map = new Map<
      string,
      {
        low: number
        medium: number
        critical: number
        timestamp: string
        label: string
        sourceDate?: string
        sourceHour?: number
      }
    >()

    for (const point of pointsForGranularity) {
      const sourceDayStart = point.sourceDate
        ? getRangeFromDateAndHour(point.sourceDate, 0, 24, timezone).start
        : undefined
      const quarterDaySegment =
        point.sourceHour === undefined
          ? undefined
          : Math.floor(Math.max(0, Math.min(23, point.sourceHour)) / 6)
      const shouldUseSourceHourRange =
        (currentGranularity === 'quarter_day' || currentGranularity === 'hour') &&
        point.sourceDate !== undefined &&
        point.sourceHour !== undefined
      const shouldUseSourceDayRange =
        !shouldUseSourceHourRange &&
        sourceDayStart !== undefined
      let t = new Date(point.timestamp).getTime()
      if (shouldUseSourceHourRange) {
        t = getRangeFromDateAndHour(point.sourceDate ?? '', point.sourceHour ?? 0, 1, timezone).start.getTime()
      } else if (shouldUseSourceDayRange) {
        t = sourceDayStart?.getTime() ?? t
      }
      if (t < from || t > to) continue
      const bucketAnchorTimestamp = sourceDayStart?.toISOString() ?? point.timestamp
      let bucketStart = getBucketRangeForGranularity(bucketAnchorTimestamp, currentGranularity).from
      if (currentGranularity === 'day' && sourceDayStart) {
        bucketStart = sourceDayStart
      }
      if (currentGranularity === 'quarter_day' && point.sourceDate && quarterDaySegment !== undefined) {
        bucketStart = getRangeFromDateAndHour(
          point.sourceDate,
          quarterDaySegment * 6,
          6,
          timezone
        ).start
      } else if (currentGranularity === 'hour' && point.sourceDate && point.sourceHour !== undefined) {
        bucketStart = getRangeFromDateAndHour(
          point.sourceDate,
          point.sourceHour,
          1,
          timezone
        ).start
      }
      const key =
        currentGranularity === 'quarter_day' &&
        point.sourceDate &&
        quarterDaySegment !== undefined
          ? `${point.sourceDate}-${quarterDaySegment}`
          : bucketStart.toISOString()
      if (!map.has(key)) {
        const quarterDayLabel =
          currentGranularity === 'quarter_day' &&
          point.sourceDate &&
          quarterDaySegment !== undefined
            ? `${formatShortDate(sourceDayStart ?? parseGraphDate(point.sourceDate), timezone)} ${QUARTER_DAY_LABEL_BY_SEGMENT[quarterDaySegment] ?? 'Evening'}`
            : undefined
        map.set(key, {
          low: 0,
          medium: 0,
          critical: 0,
          timestamp: bucketStart.toISOString(),
          label:
            quarterDayLabel ??
            formatTimestamp(bucketStart.toISOString(), currentGranularity),
          sourceDate: point.sourceDate,
          sourceHour: point.sourceHour,
        })
      }
      const bucket = map.get(key)
      if (!bucket) continue
      bucket.low += point.low
      bucket.medium += point.medium
      bucket.critical += point.critical
    }

    const entries = Array.from(map.entries()).sort((a, b) => {
      if (currentGranularity !== 'quarter_day') {
        return new Date(a[1].timestamp).getTime() - new Date(b[1].timestamp).getTime()
      }

      const sourceDayA = a[1].sourceDate
      const sourceDayB = b[1].sourceDate
      const dayDiff =
        sourceDayA && sourceDayB
          ? sourceDayA.localeCompare(sourceDayB)
          : new Date(a[1].timestamp).getTime() - new Date(b[1].timestamp).getTime()
      if (dayDiff !== 0) {
        return dayDiff
      }

      const segmentA =
        a[1].sourceHour === undefined
          ? getQuarterDaySegment(new Date(a[1].timestamp))
          : Math.floor(Math.max(0, Math.min(23, a[1].sourceHour)) / 6)
      const segmentB =
        b[1].sourceHour === undefined
          ? getQuarterDaySegment(new Date(b[1].timestamp))
          : Math.floor(Math.max(0, Math.min(23, b[1].sourceHour)) / 6)
      // API segment indices are already chronological: 0=night (00:00-05:59),
      // 1=morning, 2=afternoon, 3=evening. Sort by the raw index so each day
      // renders in clock order (night first), matching the bucket timestamps.
      return segmentA - segmentB
    })
    return {
      keys: entries.map(([k]) => k),
      labels: entries.map(([, v]) => v.label),
      low: entries.map(([, v]) => v.low),
      medium: entries.map(([, v]) => v.medium),
      critical: entries.map(([, v]) => v.critical),
      rawTs: entries.map(([, v]) => v.timestamp),
      rawDate: entries.map(([, v]) => v.sourceDate),
      rawHour: entries.map(([, v]) => v.sourceHour),
    }
  }, [pointsForGranularity, fromMs, toMs, currentGranularity, timezone, formatTimestamp])

  const drillDownToTimePoint = (index: number) => {
    const next = clampToFloor(getNextGranularity(currentGranularity), granularityFloor)
    if (next === currentGranularity) return
    if (index < 0 || index >= processed.keys.length) return
    setSelectedTimePoint(processed.keys[index])
    setLastClickedIndex(index)

    const { from, to } = getBucketRangeForGranularity(processed.rawTs[index], currentGranularity)
    let rangeStart = from
    let rangeEnd = to
    let graphRangeFrom = rangeStart.toISOString()
    let graphRangeTo = rangeEnd.toISOString()

    // week/month/quarter/year buckets are anchored at UTC boundaries and labeled
    // in UTC. Rebuild the drilldown range from those UTC calendar dates as local
    // day boundaries so the range subtitle (rendered in the user's timezone)
    // matches the clicked bucket's label instead of reading a day early for
    // negative-offset zones (e.g. label "Apr 13-19" but range "Apr 12-19").
    if (
      currentGranularity === 'week' ||
      currentGranularity === 'month' ||
      currentGranularity === 'quarter' ||
      currentGranularity === 'year'
    ) {
      const startRange = getRangeFromDateAndHour(formatUtcDate(from), 0, 24, timezone)
      const endRange = getRangeFromDateAndHour(formatUtcDate(to), 0, 24, timezone)
      rangeStart = startRange.start
      rangeEnd = endRange.end
      graphRangeFrom = rangeStart.toISOString()
      graphRangeTo = rangeEnd.toISOString()
    }

    if (currentGranularity === 'day') {
      const date = processed.rawDate[index]
      if (date) {
        const { start, end } = getRangeFromDateAndHour(date, 0, 24, timezone)
        rangeStart = start
        rangeEnd = end
        graphRangeFrom = start.toISOString()
        graphRangeTo = end.toISOString()
      }
    }

    if (currentGranularity === 'quarter_day') {
      const date = processed.rawDate[index]
      const hour = processed.rawHour[index]
      if (date && hour !== undefined) {
        const quarterStartHour = Math.floor(Math.max(0, Math.min(23, hour)) / 6) * 6
        const { start, end } = getRangeFromDateAndHour(
          date,
          quarterStartHour,
          6,
          timezone
        )
        rangeStart = start
        rangeEnd = end
        graphRangeFrom = start.toISOString()
        graphRangeTo = end.toISOString()
      }
    }

    if (next === 'event') {
      const date = processed.rawDate[index]
      const hour = processed.rawHour[index]
      if (date && hour !== undefined) {
        const { start, end } = getRangeFromDateAndHour(date, hour, 1, timezone)
        graphRangeFrom = start.toISOString()
        graphRangeTo = end.toISOString()
        setEventQueryParams({ date, hour })
      } else {
        setEventQueryParams(null)
      }
    }
    setCurrentGranularity(next)
    updateFilters({
      selectedDateRange: { from: rangeStart.toISOString(), to: rangeEnd.toISOString() },
      graphGranularityDateRange: { from: graphRangeFrom, to: graphRangeTo },
      zoomHistory: [
        ...(filters.zoomHistory ?? []),
        {
          granularity: currentGranularity,
          range: { from: resolvedFromDate ?? '', to: resolvedToDate ?? '' },
          graphRange: filters.graphGranularityDateRange ?? null,
        },
      ],
    })
  }

  const zoomOut = () => {
    const prev = filters.zoomHistory?.at(-1)
    if (!prev?.granularity || !prev.range?.from || !prev.range?.to) return
    const prevFrom = new Date(prev.range.from)
    const prevTo = new Date(prev.range.to)
    const hasValidPrevRange = !Number.isNaN(prevFrom.getTime()) && !Number.isNaN(prevTo.getTime())
    const fallbackGraphRange =
      (filters.zoomHistory?.length ?? 0) > 1 && hasValidPrevRange
        ? {
            from: prevFrom.toISOString(),
            to: prevTo.toISOString(),
          }
        : null
    const restoredGraphRange = prev.graphRange ?? fallbackGraphRange
    setEventQueryParams(null)
    setCurrentGranularity(clampToFloor(prev.granularity, granularityFloor))
    setSelectedTimePoint(null)
    updateFilters({
      selectedDateRange: { from: prev.range.from, to: prev.range.to },
      graphGranularityDateRange: restoredGraphRange,
      zoomHistory: filters.zoomHistory?.slice(0, -1),
    })
  }

  const uniqueCount = processed.labels.length
  const hasSparse = uniqueCount <= 3
  const padded = useMemo(() => {
    if (!hasSparse || uniqueCount === 0) {
      return {
        labels: [...processed.labels],
        low: [...processed.low],
        med: [...processed.medium],
        crit: [...processed.critical],
      }
    }

    const firstLabel = processed.labels[0]
    const lastLabel = processed.labels.at(-1)
    const prefix = uniqueCount === 1 ? [`Before ${firstLabel}`, `Before ${firstLabel}`] : ['1', '2']
    const suffix =
      uniqueCount === 1
        ? [`After ${lastLabel}`, `After ${lastLabel}`]
        : [`${uniqueCount + 1}`, `${uniqueCount + 2}`]

    return {
      labels: [...prefix, ...processed.labels, ...suffix],
      low: [null, null, ...processed.low, null, null],
      med: [null, null, ...processed.medium, null, null],
      crit: [null, null, ...processed.critical, null, null],
    }
  }, [processed.labels, processed.low, processed.medium, processed.critical, hasSparse, uniqueCount])

  const severityDatasets = [
    { severity: 1 as const, label: 'Low Severity', data: padded.low },
    { severity: 2 as const, label: 'Medium Severity', data: padded.med },
    { severity: 3 as const, label: 'Critical Severity', data: padded.crit },
  ]

  const chartData = {
    labels: padded.labels,
    datasets: severityDatasets
      .filter(
        (dataset) =>
          filters.selectedSeverity === null || filters.selectedSeverity === dataset.severity
      )
      .map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        borderColor: getSeverityColor(dataset.severity, 1),
        backgroundColor: getSeverityColor(dataset.severity, 0.2),
        tension: 0.4,
        pointRadius: hasSparse ? 8 : 4,
        pointHoverRadius: hasSparse ? 10 : 6,
      })),
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: { callbacks: { label: (c) => (c.raw === null ? '' : `${c.dataset.label ?? ''}: ${c.parsed.y} alerts`) }, filter: (i) => i.raw !== null },
      legend: { position: 'top' },
    },
    scales: {
      x: {
        grid: { display: true, drawOnChartArea: true },
        ticks: {
          // Never drop labels: the chart scrolls horizontally so every point keeps its tick.
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          callback: (_v, idx) =>
            isPaddingTickIndex(chartData.datasets, idx) ? '' : chartData.labels[idx],
        },
      },
      y: { beginAtZero: true, grid: { display: true, drawOnChartArea: true } },
    },
    elements: { line: { tension: 0.4, spanGaps: true, fill: false }, point: { radius: hasSparse ? 8 : 4, hoverRadius: hasSparse ? 10 : 6, hitRadius: hasSparse ? 15 : 10 } },
    onClick: (_e, elements) => {
      if (!elements.length) return
      let idx = elements[0].index
      if (hasSparse) idx -= 2
      drillDownToTimePoint(idx)
    },
  }

  /**
   * Formats the subtitle range text shown above the graph.
   * Shows time detail for sub-day windows and full dates otherwise.
   */
  const formatDateRangeDisplay = (from: string, to: string) => {
    if (!from || !to) return ''
    const start = new Date(from)
    const end = new Date(to)
    const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60)

    if (diffHours < ONE_DAY_HOURS) {
      return `${formatShortDate(start, timezone)} ${formatShortTime(start, timezone)} - ${formatShortTime(end, timezone)}`
    }

    return `${start.toLocaleDateString(undefined, { ...FULL_DATE_OPTIONS, timeZone: timezone })} - ${end.toLocaleDateString(undefined, { ...FULL_DATE_OPTIONS, timeZone: timezone })}`
  }

  // Grow the graph beyond the card width when there are many points so the chart
  // scrolls horizontally (via the wrapper) instead of dropping points. width:100%
  // keeps it filling the card when there are only a few points.
  const chartScrollMinWidth = `${padded.labels.length * POINT_SCROLL_WIDTH}px`

  const granularityLabel = currentGranularity === 'quarter_day' ? 'part of day' : currentGranularity
  const activeLoading = isEventGranularity
    ? isEventsLoading || isEventsFetching
    : isLoading || isFetching
  const activeError = isEventGranularity ? isEventsError : isError

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {!!filters.zoomHistory?.length && (
          <Tooltip title="Zoom out to previous view">
            <IconButton
              onClick={zoomOut}
              sx={{ fontSize: '0.75rem', width: 36, height: 36, mr: 2 }}
              data-qa-id="dashboard-graph-zoom-out-button"
            >
              ↩
            </IconButton>
          </Tooltip>
        )}
        <Box>
          <Typography variant="h6" component="div">
            Alerts & Severity over time
            {currentGranularity !== 'day' && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                (Showing by {granularityLabel})
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDateRangeDisplay(resolvedFromDate ?? '', resolvedToDate ?? '')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', mb: 2 }}>
        {/* Single horizontal scroll container so the chart and the click buttons
            share one scrollbar and every point stays aligned with its button. */}
        <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }} data-qa-id="dashboard-graph-scroll">
          <Box sx={{ width: '100%', minWidth: chartScrollMinWidth }}>
            <Box sx={{ height: 300, width: '100%' }}>
              {processed.keys.length === 0 ? (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 1,
                  }}
                >
                  {!activeLoading && (
                    <Typography variant="body1" color="text.secondary">
                      {activeError
                        ? 'Failed to load graph data'
                        : 'No data available for the selected time period'}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Line data={chartData} options={options} />
              )}
            </Box>

            {processed.keys.length > 0 && currentGranularity !== 'event' && (
              <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center" sx={{ py: 1, px: 2, mt: 1 }}>
                {processed.keys.map((key, index) => {
                  const total = (processed.low[index] || 0) + (processed.medium[index] || 0) + (processed.critical[index] || 0)
                  return (
                    <Tooltip key={key} title={`${processed.labels[index]}: ${total} alerts`}>
                      <IconButton
                        onClick={() => drillDownToTimePoint(index)}
                        data-qa-id={`dashboard-graph-point-button-${index}`}
                        sx={{
                          width: 36,
                          height: 36,
                          minWidth: 36,
                          border: '1px solid',
                          borderColor: selectedTimePoint === key ? 'primary.main' : 'divider',
                          backgroundColor: selectedTimePoint === key ? 'primary.main' : 'background.paper',
                          color: selectedTimePoint === key ? 'primary.contrastText' : 'text.primary',
                          '&:hover': { backgroundColor: selectedTimePoint === key ? 'primary.dark' : 'action.hover' },
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                          mx: 0.5,
                        }}
                      >
                        {total}
                      </IconButton>
                    </Tooltip>
                  )
                })}
              </Stack>
            )}
          </Box>
        </Box>
        {activeLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.55)',
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export default DashboardGraph
